// Soundscapes are seamless-looping Opus 96k beds. Authoring/encoding procedure
// (per-file silence trim + 0.5s qsin loop crossfade) is documented in
// .claude/wiki/wiki/projects/audio.md; follow it before adding or replacing any bed.
const SOUNDSCAPES = {
  ds_craine_car_briefing: '/audio/brief_car.opus',
  ds_craine_car_debrief: '/audio/debrief_car.opus',
  ds_helios_lobby: '/audio/helios_lobby.opus',
  ds_helios_office_establishing: '/audio/hargrove_office_establishing.opus',
  ds_helios_office_interview: '/audio/hargrove_interview.opus',
  ds_helios_elevator: '/audio/helios_elevator.opus',
};

const OUTTRO_URL = '/audio/demo_outtro.mp3';
const BOOT_TYPING_URL = '/audio/boot_typing_digital.wav';

const SFX_URLS = {
  type: '/audio/sfx_type_click.mp3',
  navigation: '/audio/sfx_navigation.mp3',
  systemAlert: '/audio/sfx_system_alert.mp3',
  success: '/audio/sfx_success.mp3',
  hudAlert: '/audio/sfx_hud_alert.mp3',
  elevatorDing: '/audio/sfx_elevator_ding.mp3',
  heartbeat: '/audio/sfx_heartbeat.mp3',
  transitionIn: '/audio/sfx_transition_in.mp3',
  radioClick: '/audio/sfx_radio_click.mp3',
  policeChirp: '/audio/sfx_police_chirp.mp3',
};

// One-shot static bed played while the first radio song buffers; the file
// carries its own fade-in/out, so it self-terminates if the song beats it.
const RADIO_STATIC_URL = '/audio/radio_static.mp3';

function gainToDb(level) {
  if (level <= 0) return -100;
  return 20 * Math.log10(level);
}

/**
 * Pure mapping from a scene/location id to its soundscape key. Shared by
 * setRoomTone (when the scene mounts) and preBleedForLocation (during the
 * black transition, so the tone leads the picture). Returns null for scenes
 * with no ambient bed (e.g. ds_elevator_up, the animated ascent cutscene).
 * The explorable elevator car (ds_elevator_down) DOES carry a bed.
 */
function roomToneKeyFor(locationId, { carMode = 'briefing', interviewActive = false } = {}) {
  switch (locationId) {
    case 'ds_craine_car':
      return carMode === 'debrief' ? 'ds_craine_car_debrief' : 'ds_craine_car_briefing';
    case 'ds_helios_lobby':
      return 'ds_helios_lobby';
    case 'ds_helios_office':
      return interviewActive ? 'ds_helios_office_interview' : 'ds_helios_office_establishing';
    case 'ds_elevator_down':
      return 'ds_helios_elevator';
    default:
      return null;
  }
}

function fadeGain(gainNode, targetVal, durationSec) {
  const now = gainNode.context.currentTime;
  gainNode.gain.cancelScheduledValues(now);
  gainNode.gain.setValueAtTime(gainNode.gain.value, now);
  gainNode.gain.linearRampToValueAtTime(Math.max(0, targetVal), now + durationSec);
}

class AudioManager {
  constructor() {
    this.isInitialized = false;
    this._ctx = null;
    this._masterGain = null;
    this._sfxGain = null;
    this._soundscapeGain = null;

    this._sfxBuffers = {};
    this._soundscapeSource = null;
    this._soundscapeKey = null;
    this._outtroSource = null;
    this._bootTypingSource = null;
    this._heartbeatSource = null;
    this._radioSource = null;
    this._radioStaticSource = null;
    this._pendingPlays = [];
  }

  async start() {
    if (this.isInitialized) {
      if (this._ctx && this._ctx.state === 'suspended') {
        await this._ctx.resume();
      }
      this._retryPendingPlays();
      return;
    }
    if (this._starting) return this._starting;
    this._starting = (async () => {
      this._ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (this._ctx.state === 'suspended') await this._ctx.resume();

      this._masterGain = this._ctx.createGain();
      this._masterGain.connect(this._ctx.destination);

      this._sfxGain = this._ctx.createGain();
      this._sfxGain.connect(this._masterGain);

      this._soundscapeGain = this._ctx.createGain();
      this._soundscapeGain.gain.value = 0.5;
      this._soundscapeGain.connect(this._masterGain);

      this.isInitialized = true;
      this._starting = null;

      this._loadSfxBuffers();
    })();
    return this._starting;
  }

  _loadSfxBuffers() {
    Object.entries(SFX_URLS).forEach(async ([key, url]) => {
      try {
        const resp = await fetch(url);
        const buf = await resp.arrayBuffer();
        this._sfxBuffers[key] = await this._ctx.decodeAudioData(buf);
      } catch (_) {}
    });
  }

  _retryPendingPlays() {
    const pending = this._pendingPlays.splice(0);
    for (const audio of pending) {
      audio.play().catch(() => {});
    }
  }

  // ── Looping source helper ────────────────────────

  _createLoopSource(url, gainNode, { loop = true, fadeIn = 0, startGain = 1 } = {}) {
    const audio = new Audio(url);
    audio.loop = loop;
    audio.crossOrigin = 'anonymous';
    const source = this._ctx.createMediaElementSource(audio);
    const envelope = this._ctx.createGain();
    envelope.gain.value = fadeIn > 0 ? 0 : startGain;
    source.connect(envelope);
    envelope.connect(gainNode);
    audio.play().catch(() => {
      this._pendingPlays.push(audio);
    });
    if (fadeIn > 0) fadeGain(envelope, startGain, fadeIn);
    return { audio, source, envelope };
  }

  _fadeOutAndStop(ref, durationSec = 1.5) {
    if (!ref) return;
    fadeGain(ref.envelope, 0, durationSec);
    setTimeout(() => {
      try { ref.audio.pause(); } catch (_) {}
      try { ref.source.disconnect(); } catch (_) {}
      try { ref.envelope.disconnect(); } catch (_) {}
    }, durationSec * 1000 + 200);
  }

  _hardStop(ref) {
    if (!ref) return;
    try { ref.envelope.gain.cancelScheduledValues(ref.envelope.context.currentTime); } catch (_) {}
    try { ref.envelope.gain.value = 0; } catch (_) {}
    try { ref.audio.pause(); } catch (_) {}
    try { ref.source.disconnect(); } catch (_) {}
    try { ref.envelope.disconnect(); } catch (_) {}
  }

  // ── Soundscape management ─────────────────────────

  _stopSoundscape() {
    if (this._soundscapeSource) {
      this._fadeOutAndStop(this._soundscapeSource, 1.5);
      this._soundscapeSource = null;
      this._soundscapeKey = null;
    }
  }

  _playSoundscape(key) {
    if (this._soundscapeKey === key) {
      if (this._soundscapeSource?.audio && !this._soundscapeSource.audio.paused) return;
    }
    this._stopSoundscape();
    const url = SOUNDSCAPES[key];
    if (!url) return;
    this._soundscapeKey = key;
    this._soundscapeSource = this._createLoopSource(url, this._soundscapeGain, { fadeIn: 0.8 });
  }

  startPreBleed(key) {
    if (!this.isInitialized) return;
    if (this._soundscapeKey === key) return;
    this._stopSoundscape();
    const url = SOUNDSCAPES[key];
    if (!url) return;
    this._soundscapeKey = key;
    this._soundscapeSource = this._createLoopSource(url, this._soundscapeGain, { startGain: 0.01, fadeIn: 0 });
    // Ramp up across the ~1s black transition so the bed is clearly present
    // the moment the destination scene appears.
    fadeGain(this._soundscapeSource.envelope, 1, 1.4);
  }

  setRoomTone(locationId, opts = {}) {
    if (!this.isInitialized) return;
    const key = roomToneKeyFor(locationId, opts);
    if (!key) { this._stopSoundscape(); return; }
    this._playSoundscape(key);
  }

  // Begin a destination room tone during a scene transition so it is already
  // audible the instant the scene appears (audio leads picture). No-op if the
  // audio context isn't unlocked yet or that tone is already playing.
  preBleedForLocation(locationId, opts = {}) {
    if (!this.isInitialized) return;
    const key = roomToneKeyFor(locationId, opts);
    if (key) this.startPreBleed(key);
  }

  // ── Outtro music ──────────────────────────────────

  playOuttro({ loop = false } = {}) {
    if (!this.isInitialized) return;
    this.stopOuttro();
    this._outtroSource = this._createLoopSource(OUTTRO_URL, this._soundscapeGain, { loop, fadeIn: 1 });
  }

  stopOuttro() {
    if (this._outtroSource) {
      this._fadeOutAndStop(this._outtroSource, 1.5);
      this._outtroSource = null;
    }
  }

  // ── Boot typing ambient SFX ───────────────────────

  playBootTyping() {
    if (!this.isInitialized) return;
    this.stopBootTyping();
    this._bootTypingSource = this._createLoopSource(BOOT_TYPING_URL, this._sfxGain, { fadeIn: 0.3 });
  }

  stopBootTyping() {
    if (this._bootTypingSource) {
      this._fadeOutAndStop(this._bootTypingSource, 0.5);
      this._bootTypingSource = null;
    }
  }

  // ── Heartbeat (establishing screen) ───────────────

  playHeartbeat() {
    if (!this.isInitialized) return;
    this.stopHeartbeat();
    // Looped so a lengthened establishing-screen dwell (HEARTBEAT_HOLD_MS in
    // BootScreen) keeps beating instead of going silent after the one-shot clip.
    // stopHeartbeat() fades it out on the way into the car.
    this._heartbeatSource = this._createLoopSource(SFX_URLS.heartbeat, this._sfxGain, { loop: true });
  }

  stopHeartbeat() {
    if (this._heartbeatSource) {
      this._fadeOutAndStop(this._heartbeatSource, 1.5);
      this._heartbeatSource = null;
    }
  }

  // ── Volume controllers (0–1 scale) ────────────────

  setMasterVolume(level) {
    if (!this.isInitialized) return;
    this._masterGain.gain.value = level;
  }

  setSFXVolume(level) {
    if (!this.isInitialized) return;
    this._sfxGain.gain.value = level;
  }

  setSoundscapeVolume(level) {
    if (!this.isInitialized) return;
    this._soundscapeGain.gain.value = level;
  }

  // ── SFX (buffer-based, instant playback) ──────────

  _playSfx(key, gain = 1) {
    if (!this.isInitialized) return;
    const buffer = this._sfxBuffers[key];
    if (!buffer) return;
    const source = this._ctx.createBufferSource();
    source.buffer = buffer;
    // Optional per-sound attenuation for buffers that were authored hot.
    if (gain !== 1) {
      const g = this._ctx.createGain();
      g.gain.value = gain;
      source.connect(g);
      g.connect(this._sfxGain);
    } else {
      source.connect(this._sfxGain);
    }
    source.start();
  }

  playType()        { this._playSfx('type'); }
  playNavigation()  { this._playSfx('navigation'); }
  playSystemAlert() { this._playSfx('systemAlert'); }
  playSuccess()     { this._playSfx('success', 0.4); } // evidence chime: file is hot, attenuate
  playHUDAlert()    { this._playSfx('hudAlert'); }
  playElevatorDing(){ this._playSfx('elevatorDing'); }
  playRadioClick()  { this._playSfx('radioClick'); }
  playPoliceChirp() { this._playSfx('policeChirp'); }
  playTransitionIn(){ this._playSfx('transitionIn'); }

  // ── Car radio easter egg ──────────────────────────

  startCarRadio(songFile, onEnded, onPlaying, { staticIntro = false } = {}) {
    if (!this.isInitialized) return;
    // Fade the room tone down to a whisper so the ambience isn't fully lost
    if (this._soundscapeSource) {
      fadeGain(this._soundscapeSource.envelope, 0.05, 0.8);
    }
    // Crossfade: fade old track out while new one fades in
    if (this._radioSource) {
      this._fadeOutAndStop(this._radioSource, 0.8);
    }
    // Tuning static covers the buffering gap before each track (switch-on and
    // between songs). The one-shot bed self-terminates; the song's `playing`
    // event crossfades it out early when the stream beats the bed's own fade-out.
    if (staticIntro) this._startRadioStatic();
    this._radioSource = this._createLoopSource(songFile, this._soundscapeGain, { loop: false, fadeIn: 0.8 });
    this._radioSource.audio.addEventListener('playing', () => this._stopRadioStatic(0.3), { once: true });
    if (onPlaying) {
      this._radioSource.audio.addEventListener('playing', onPlaying, { once: true });
    }
    if (onEnded) {
      // Advance on a clean end, but also on a load/decode failure: a track that
      // 404s or won't decode fires `error` and never `ended`, which would
      // otherwise hang the radio on silence and let the tracks that DO load
      // dominate what the player hears. Guard so we only advance once.
      let advanced = false;
      const advance = () => { if (advanced) return; advanced = true; onEnded(); };
      this._radioSource.audio.addEventListener('ended', advance, { once: true });
      this._radioSource.audio.addEventListener('error', () => { this._stopRadioStatic(0.3); advance(); }, { once: true });
    }
  }

  _startRadioStatic() {
    this._stopRadioStatic(0);
    this._radioStaticSource = this._createLoopSource(RADIO_STATIC_URL, this._soundscapeGain, { loop: false, fadeIn: 0.1, startGain: 0.7 });
  }

  _stopRadioStatic(fadeSec) {
    const ref = this._radioStaticSource;
    if (!ref) return;
    this._radioStaticSource = null;
    if (fadeSec > 0) this._fadeOutAndStop(ref, fadeSec);
    else this._hardStop(ref);
  }

  stopCarRadio() {
    if (!this.isInitialized) return;
    this._stopRadioStatic(0);
    if (this._radioSource) {
      this._hardStop(this._radioSource);
      this._radioSource = null;
    }
    if (this._soundscapeSource) {
      fadeGain(this._soundscapeSource.envelope, 1, 0.3);
    }
  }

  getSoundscapeVolume() {
    if (!this.isInitialized) return 0;
    return this._soundscapeGain.gain.value;
  }
}

export const AudioSystem = new AudioManager();
